/**
 * SearchSuggestionsDropdown — portal-based autocomplete suggestions list.
 *
 * Reusable dropdown for Google Places autocomplete results.
 * Used by AddressSearchField and GooglePlacesBusinessAutocomplete.
 *
 * Renders via createPortal for overflow safety (same pattern as Picker).
 */

import { forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { MapPin } from 'lucide-react';
import { ds } from '../tokens';

export interface SuggestionItem {
  id: string;
  primaryText: string;
  secondaryText?: string;
}

interface SearchSuggestionsDropdownProps {
  items: SuggestionItem[];
  onSelect: (id: string) => void;
  position: { top?: number; bottom?: number; left: number; width: number };
  /** Leading icon component per row (default: MapPin). Rendered in system blue by the component. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Header hint text */
  hint?: string;
  /** Max height of the entire dropdown in px (default: 340) */
  maxHeight?: number;
  /** Render inline (positioned within parent) instead of as a portal. Use inside modals/sheets. */
  inline?: boolean;
}

export const SearchSuggestionsDropdown = forwardRef<HTMLDivElement, SearchSuggestionsDropdownProps>(
  function SearchSuggestionsDropdown(
    {
      items,
      onSelect,
      position,
      icon,
      hint = 'Select to auto-fill details',
      maxHeight = 340,
      inline = false,
    },
    ref,
  ) {
    if (items.length === 0) return null;

    const IconComponent = icon || MapPin;
    const iconClasses = [ds.icon.sm, ds.color.label.link, 'flex-shrink-0 mt-1'].join(' ');

    const effectiveMaxHeight = inline ? Math.min(maxHeight, 260) : maxHeight;

    const dropdown = (
      <div
        ref={ref}
        className={[
          inline ? 'relative mt-1' : 'fixed',
          'overflow-hidden py-1 flex flex-col',
          inline ? 'z-10' : ds.zIndex.dropdown,
          ds.color.fill.primary,
          ds.radius.md,
          'border',
          ds.color.separator.light,
          ds.shadow.menu,
        ].join(' ')}
        style={{
          ...(inline ? {} : {
            ...(position.bottom !== undefined ? { bottom: position.bottom } : { top: position.top }),
            left: position.left,
            width: position.width,
          }),
          maxHeight: effectiveMaxHeight,
        }}
      >
        {/* Header hint */}
        <div className={['px-4 py-2 border-b flex-shrink-0', ds.color.separator.light].join(' ')}>
          <p className={[ds.type.caption1, ds.color.label.secondary].join(' ')}>
            {hint}
          </p>
        </div>

        {/* Scrollable suggestion list */}
        <div className={`flex-1 min-h-0 overflow-y-auto px-1.5 ${ds.layout.scrollbar}`}>
          {items.map(item => (
            <button
              key={item.id}
              type="button"
              onMouseDown={e => { e.preventDefault(); onSelect(item.id); }}
              className={[
                `w-full text-left px-3 py-3 flex items-start gap-3 ${ds.radius.md}`,
                ds.transition.fast,
                ds.color.fill.hover,
              ].join(' ')}
            >
              <IconComponent className={iconClasses} />
              <div className="flex-1 min-w-0">
                <div className={[ds.type.bodyMedium, ds.color.label.primary, 'line-clamp-1'].join(' ')}>
                  {item.primaryText}
                </div>
                {item.secondaryText && (
                  <div className={[ds.type.caption1, ds.color.label.secondary, 'mt-0.5 line-clamp-1'].join(' ')}>
                    {item.secondaryText}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className={['px-4 py-2 border-t flex-shrink-0 flex items-center justify-between', ds.color.separator.light].join(' ')}>
          <span className={[ds.type.caption1, ds.color.label.secondary].join(' ')}>
            {items.length} {items.length === 1 ? 'match' : 'matches'} found
          </span>
          <span className={[ds.type.caption2, ds.color.label.tertiary].join(' ')}>
            Powered by Google
          </span>
        </div>
      </div>
    );

    return inline ? dropdown : createPortal(dropdown, document.body);
  },
);
